import { useEffect, useState } from 'react';
import { API } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, Button, makeStyles } from '@material-ui/core';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import useUIState from '../../hooks/useUIstate';
import TraceBlocksTable from './TraceBlocksTable';
import { TraceBlockRow } from '../../interfaces/ITraceBlock';

const useStyles = makeStyles(() => ({
  filters: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap',
    '& .filter-group': {
      display: 'flex',
      flexDirection: 'column',
    },
    '& label': {
      fontSize: '0.8rem',
      marginBottom: '4px',
    },
    '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
      minWidth: '200px',
    },
  },
}));

function defaultStartDate(): Date {
  const date = new Date();
  date.setDate(date.getDate());
  return date;
}

function TraceBlocksScreen() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { setShowLoadingModal, setToastMessage, setShowToastMessage, setToastType } = useUIState();

  const [stores, setStores] = useState<any[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [blocks, setBlocks] = useState<any[]>([]);
  const [selectedLogicalName, setSelectedLogicalName] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate());
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [traceRows, setTraceRows] = useState<TraceBlockRow[]>([]);

  useEffect(() => {
    localStorage.setItem('activeRoute', '/trace-blocks');
    API.get('AE', '/Stores', { response: true })
      .then(res => setStores(res.data))
      .catch(error => console.log(error));
  }, []);

  useEffect(() => {
    setSelectedLogicalName('');
    setTraceRows([]);
    if (!selectedStoreId) {
      setBlocks([]);
      return;
    }
    API.get('AE', `/Blocks?storeId=${selectedStoreId}`, { response: true })
      .then(res => setBlocks(res.data))
      .catch(error => console.log(error));
    // eslint-disable-next-line
  }, [selectedStoreId]);

  const logicalNameOptions = Array.from(new Set(blocks.map(b => b.LogicalName))).filter(Boolean).sort();

  async function search() {
    setShowLoadingModal(true);
    API.get('AE', '/TraceBlocks', {
      response: true,
      queryStringParameters: {
        storeId: selectedStoreId,
        logicalName: selectedLogicalName,
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
      },
    })
      .then(res => setTraceRows(res.data))
      .catch(error => {
        console.log(error);
        setToastType('error');
        setToastMessage(t('traceblocks.error.getting-trace'));
        setShowToastMessage(true);
      })
      .finally(() => setShowLoadingModal(false));
  }

  return (
    <div>
      <h1>{t('traceblocks.title')}</h1>
      <div className={classes.filters}>
        <div className="filter-group">
          <label>{t('traceblocks.store')}</label>
          <Select
            value={selectedStoreId}
            onChange={e => setSelectedStoreId(e.target.value as string)}
            variant="outlined"
            displayEmpty
          >
            <MenuItem value="">{t('traceblocks.select-store')}</MenuItem>
            {stores.map(store => (
              <MenuItem key={store.Id} value={store.Id}>{store.Code}</MenuItem>
            ))}
          </Select>
        </div>
        <div className="filter-group">
          <label>{t('traceblocks.logical-name')}</label>
          <Select
            value={selectedLogicalName}
            onChange={e => setSelectedLogicalName(e.target.value as string)}
            variant="outlined"
            displayEmpty
            disabled={!selectedStoreId}
          >
            <MenuItem value="">{t('traceblocks.select-logical-name')}</MenuItem>
            {logicalNameOptions.map(logicalName => (
              <MenuItem key={logicalName} value={logicalName}>{logicalName}</MenuItem>
            ))}
          </Select>
        </div>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <div className="filter-group">
            <label>{t('traceblocks.from')}</label>
            <DateTimePicker
              clearable
              autoOk
              ampm={false}
              format="dd/MM/yyyy HH:mm"
              value={startDate}
              onChange={setStartDate}
            />
          </div>
          <div className="filter-group">
            <label>{t('traceblocks.to')}</label>
            <DateTimePicker
              clearable
              autoOk
              ampm={false}
              format="dd/MM/yyyy HH:mm"
              value={endDate}
              onChange={setEndDate}
            />
          </div>
        </MuiPickersUtilsProvider>
        <div className="filter-group">
          <label>&nbsp;</label>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedStoreId || !selectedLogicalName}
            onClick={search}
          >
            {t('traceblocks.search')}
          </Button>
        </div>
      </div>
      <TraceBlocksTable rows={traceRows} storeId={Number(selectedStoreId)} />
    </div>
  );
}

export default TraceBlocksScreen;
